import { Image } from '@unistocks/components/basic/image';
import { Column } from '@unistocks/components/layout/flex';
import { EventType, EventSubType } from '@unistocks/proxy/axios-client';
import { formatCurrency, t } from '@unistocks/shared/localization/index.ts';
import { mapEnumToOptions } from '@unistocks/shared/utils/enum-utils';
import { withFeedback } from '@unistocks/shared/utils/haptics/with-feedback';
import { ImageSourcePropType } from 'react-native';
import { ListItem, ListItemProps } from 'tamagui';

const eventTypeOptions = mapEnumToOptions(EventType, 'EventType').reduce(
  (acc, { key, value }) => ({ ...acc, [value]: key }),
  {} as Record<EventType, string>
);

const eventSubTypeOptions = mapEnumToOptions(EventSubType, 'EventSubType').reduce(
  (acc, { key, value }) => ({ ...acc, [value]: key }),
  {} as Record<EventSubType, string>
);

export type EventListItemProps = {
  type: EventType;
  subType?: EventSubType | null;
  title?: string;
  image?: ImageSourcePropType | null;
  value?: { value: number; currency: string } | null;
} & Omit<ListItemProps, 'title' | 'icon'>;

export const EventListItem: React.FC<EventListItemProps> = ({
  type,
  subType,
  title,
  image,
  value,
  ...props
}) => {
  const getSubTitle = () => {
    let subtitle: string;

    switch (type) {
      case EventType.Dividend:
        subtitle = t(eventTypeOptions[type]);
        if (subType && eventSubTypeOptions[subType])
          return `${subtitle}, ${t(eventSubTypeOptions[subType])}`;
        return subtitle;

      case EventType.Earnings:
        return subType ? t(eventSubTypeOptions[subType]) : t(eventTypeOptions[type]);

      case EventType.GeneralMeeting:
        return subType ? t(eventSubTypeOptions[subType]) : t(eventTypeOptions[type]);

      default:
        return t(eventTypeOptions[type]);
    }
  };

  return (
    <ListItem
      cursor={props.onPress ? 'pointer' : 'default'}
      gap={'$3'}
      title={title}
      subTitle={getSubTitle()}
      icon={image ? <Image rounded size={'$2.5'} source={image} /> : undefined}
      iconAfter={
        value ? (
          <Column>
            <ListItem.Text fontWeight="$7">
              {formatCurrency(value.value, value.currency)}
            </ListItem.Text>
            <ListItem.Subtitle>{t('::PerShare').toLocaleLowerCase()}</ListItem.Subtitle>
          </Column>
        ) : undefined
      }
      {...props}
      onPressIn={props.onPress ? withFeedback : undefined}
    />
  );
};

EventListItem.displayName = 'EventListItem';
